import { translate } from '../../../../i18n';
import { ReactComponent as DepartureIcon } from '../../../assets/progress-active-icon.svg';
import { ReactComponent as DestinationIcon } from '../../../assets/progress-future-icon.svg';
import {
  FirstMile,
  LastMile,
  MiddleMile,
} from '../../../models/delivery-options/delivery-miles';
import { DeliveryOption } from '../../../models/delivery-options/delivery-option';

export type DeliveryProgressType = 'departure' | 'destination';

type Icon = {
  [key in DeliveryProgressType]: React.ReactNode;
};

const icons: Icon = {
  departure: <DepartureIcon />,
  destination: <DestinationIcon />,
};

export const getIcon = (progress: DeliveryProgressType) => icons[progress];

export function compareMiles(
  a: FirstMile | MiddleMile | LastMile | undefined,
  b: FirstMile | MiddleMile | LastMile | undefined
) {
  return (
    a?.service.code === b?.service.code &&
    a?.carrier.code === b?.carrier.code &&
    a?.delivery_service?.slug === b?.delivery_service?.slug
  );
}

export function compareOptions(
  a?: DeliveryOption,
  b?: DeliveryOption,
  compareLastMiles: boolean = true
) {
  const firstMilesMatch = !!(
    a?.first_miles.length === b?.first_miles.length &&
    a?.first_miles.every((x, i) => compareMiles(x, b?.first_miles[i]))
  );

  const middleMileMatch = compareMiles(a?.middle_mile, b?.middle_mile);

  if (!compareLastMiles) return firstMilesMatch && middleMileMatch;

  const lastMilesMatch =
    a?.last_miles.length === b?.last_miles.length &&
    a?.last_miles.every((x, i) => compareMiles(x, b?.last_miles[i]));

  return !!(firstMilesMatch && middleMileMatch && lastMilesMatch);
}

function getDeliveryTimeMetricString(
  numOfTimePeriods: number,
  metric?: string
) {
  if (!metric) return '';

  const metricKeyByType: Record<string, string> = {
    days: 'delivery_time_unit_days',
    weeks: 'delivery_time_unit_weeks',
    months: 'delivery_time_unit_months',
    working_days: 'delivery_time_unit_working_days',
  };

  const metricKey = metricKeyByType[metric];

  if (metricKey) {
    return translate(metricKey, { count: numOfTimePeriods });
  }

  return translate(metric);
}

export function getDeliveryTimeMessage(option: DeliveryOption): string {
  const first = option.selectedFirstMile;
  const middle = option.middle_mile;
  const last = option.selectedLastMile;

  if (!first || !middle || !last) return '';

  const deliveryTimeType = option.behaviour?.find(
    (b) => b.name === 'delivery_time'
  )?.value; // static, from, to, range

  const metric = option.behaviour?.find(
    (b) => b.name === 'delivery_metric'
  )?.value; // days, weeks, working days, month

  if (deliveryTimeType === 'static') {
    return middle.static_delivery_time?.delivery_time_details ?? '';
  }

  const from =
    (first.delivery_time?.delivery_time_from ?? 0) +
    (middle.delivery_time?.delivery_time_from ?? 0) +
    (last.delivery_time?.delivery_time_from ?? 0);

  const to =
    (first.delivery_time?.delivery_time_to ?? 0) +
    (middle.delivery_time?.delivery_time_to ?? 0) +
    (last.delivery_time?.delivery_time_to ?? 0);

  const areEqual = from === to;

  if (areEqual || deliveryTimeType === 'from') {
    return `${from} ${getDeliveryTimeMetricString(from, metric)}`;
  }

  if (deliveryTimeType === 'to') {
    return `${to} ${getDeliveryTimeMetricString(to, metric)}`;
  }

  if (deliveryTimeType === 'range') {
    return `${from} - ${to} ${getDeliveryTimeMetricString(to, metric)}`;
  }

  return '';
}
